import { ShieldCheckIcon } from '@phosphor-icons/react';

import { formatPrice } from '@/lib/format';

import type { OrderLineItem, OrderTotals } from '../types';

import styles from './order-detail.module.scss';

type Props = {
  lineItems: OrderLineItem[];
  totals: OrderTotals;
  /** Phase 4: opens the Add Parameters modal for an unconfigured device. */
  onAddParameters?: (lineItemId: string) => void;
};

function LineItemRow({
  item,
  onAddParameters,
}: {
  item: OrderLineItem;
  onAddParameters?: (lineItemId: string) => void;
}) {
  return (
    <div className={styles.lineItem}>
      {item.imageUrl ? (
        <img className={styles.thumb} src={item.imageUrl} alt="" />
      ) : (
        <div className={styles.thumb} aria-hidden />
      )}
      <div className={styles.lineMain}>
        <p className={styles.lineName}>{item.name}</p>
        <div className={styles.lineLinks}>
          {item.parameterApplicable &&
            (item.parameterConfigured ? (
              <span className={styles.configured}>Configured</span>
            ) : (
              <button
                type="button"
                className={styles.placeholderLink}
                disabled={!onAddParameters}
                onClick={onAddParameters ? () => onAddParameters(item.id) : undefined}
              >
                + Add Parameter Template
              </button>
            ))}
          {/* Placeholder — owned by the Installation Request feature. */}
          {item.hasInstallationRequest && (
            <button type="button" className={styles.placeholderLink} disabled>
              View Installation
            </button>
          )}
          {item.installationRequestCount > 0 && (
            <span className={styles.badge}>
              <ShieldCheckIcon size={14} />
              {item.installationRequestCount} requested
            </span>
          )}
        </div>
        {item.bundleDevices && (
          <ul className={styles.bundleDevices}>
            {item.bundleDevices.map((d) => (
              <li key={d.name}>
                {d.quantity} × {d.name}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={styles.linePrice}>
        <span className={styles.qty}>Qty {item.quantity}</span>
        <span className={styles.price}>{formatPrice(item.unitPrice)}</span>
        {item.installationFee != null && (
          <span className={styles.installFee}>
            Installation : {formatPrice(item.installationFee)}
          </span>
        )}
      </div>
    </div>
  );
}

export function OrderSummaryCard({ lineItems, totals, onAddParameters }: Props) {
  return (
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>Order Summary</h2>
      <div className={styles.lineItems}>
        {lineItems.map((item) => (
          <LineItemRow key={item.id} item={item} onAddParameters={onAddParameters} />
        ))}
      </div>
      <dl className={styles.totals}>
        <div>
          <dt>Order Price</dt>
          <dd>{formatPrice(totals.orderPrice)}</dd>
        </div>
        <div>
          <dt>Installation Fees</dt>
          <dd>{formatPrice(totals.installationFees)}</dd>
        </div>
        <div>
          <dt>Tax</dt>
          <dd>{formatPrice(totals.tax)}</dd>
        </div>
        <div className={styles.totalRow}>
          <dt>Total</dt>
          <dd>{formatPrice(totals.total)}</dd>
        </div>
      </dl>
    </section>
  );
}
